import React, { useState } from 'react'
import ReactDom from 'react-dom'
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom'
import App from './App'
import { StoreProvider } from './store'
import './styles/main.scss'

import EnsureLoggedIn from './login/ensureLoggedIn'
import Dashboard from './dashboard'
import Login from './login/login'
import ForgotPassword from './login/ForgotPassword'
import ResetPassword from './login/ResetPassword'
import Register from './register/Register'
import Wizard from './setupWizard/Wizard'
import Claim from './claim/Claim'
import CustomerView from './customer/CustomerView'
import CustomerList from './customer/CustomerList'

import LogoInput from './setupWizard/LogoInput'
import InviteUsers from './setupWizard/InviteUsers'
import PipelineSetup from './setupWizard/PipelineSetup'
import CustomerProfileSetup from './setupWizard/CustomerProfileSetup'
import CustomFieldsSetup from './setupWizard/CustomFieldsSetup'
import ContactTypes from './setupWizard/ContactTypes'
import LeadImportWizardSetup from './setupWizard/LeadImportSetup'

import theme from './styles/theme'
import { ThemeProvider } from '@emotion/react'
import ErrorBoundary from './error-boundary'
import ActionTypes from './setupWizard/ActionTypes'
import Products from './setupWizard/Products'
import MagicButton from './setupWizard/MagicButton'
import Settings from './settings'
import LogoForm from './settings/Logo'
import Users from './settings/Users'
import AlertListener from './alert-listener'
import BusinessType from './settings/BusinessType'
import Dropdowns from './settings/Dropdowns'
import CustomFields from './settings/CustomFields'
import LeadImportSetup from './settings/LeadImportSetup'

import ContactTypesSettings from './settings/ContactTypes'
import NextSteps from './settings/NextSteps'
import ProductsSettings from './settings/Products'
import Pipelines from './settings/Pipelines'
import Profile from './profile'
import PipelineView from './pipeline/PipelineView'
import SupportView from './support/SupportView'
import ListsView from './lists/ListsView'
import Admin from './register/Admin'
import CustomerImport from './settings/CustomerImport'
import Billing from './billing/Billing'
import ModalListener from './modal-listener'

const withState = (Component) => {
  const StatefulComponent = () => {
    const [state, setState] = useState({})
    const updateState = (mergeObj) =>
      setState((prev) => ({ ...prev, ...mergeObj }))

    return <Component state={state} updateState={updateState} />
  }

  return <StatefulComponent />
}

ReactDom.render(
  <BrowserRouter>
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <StoreProvider>
          <AlertListener />
          <ModalListener />
          <Routes>
            {/* Public routes */}
            <Route exact path='/login' element={<Login />} />
            <Route exact path='/register' element={<Register />} />
            <Route exact path='/register-admin' element={<Admin />} />
            <Route exact path='/claim/:code' element={<Claim />} />

            <Route exact path='/forgot' element={<ForgotPassword />} />
            <Route path='/reset/:code' element={<ResetPassword />} />
            <Route path='/user-not-found' element={<>not found!</>} />
            {/* ************* */}

            {/* Private Routes */}
            <Route
              exact
              path='/'
              element={<Navigate to='/dashboard/todos' />}
            />
            <Route
              path={'/'}
              element={
                <EnsureLoggedIn>
                  <App />
                </EnsureLoggedIn>
              }
            >
              <Route
                path={'/dashboard/:subPath'}
                element={<Dashboard />}
              ></Route>

              <Route path={'/billing'} element={<Billing />}></Route>

              <Route path={'/profile'} element={<Profile />}></Route>
              <Route path={'/cardwall'} element={<PipelineView />}></Route>

              <Route path={'/customers'} element={<CustomerList />}></Route>

              <Route path={'/customers/:id'} element={<CustomerView />}></Route>
              <Route path={'/lists'} element={<ListsView />}></Route>
              <Route path={'/superadmin'} element={<></>} />
              <Route path={'/support'} element={<SupportView />} />

              <Route exact path={'/settings'} element={<Settings />}>
                <Route path={'/settings/logo'} element={withState(LogoForm)} />
                <Route path={'/settings/users'} element={withState(Users)} />
                <Route
                  path={'/settings/account-import'}
                  element={<CustomerImport />}
                />
                <Route path={'/settings/dropdowns'} element={<Dropdowns />} />
                <Route path={'/settings/actions'} element={<NextSteps />} />
                <Route path={'/settings/pipelines'} element={<Pipelines />} />
                <Route
                  path={'/settings/products'}
                  element={<ProductsSettings />}
                />
                <Route
                  path={'/settings/contacts'}
                  element={<ContactTypesSettings />}
                />
                <Route
                  path={'/settings/custom-fields'}
                  element={<CustomFields />}
                />
                <Route
                  path={'/settings/business-type'}
                  element={<BusinessType />}
                />
                <Route
                  path={'/settings/lead-import'}
                  element={withState(LeadImportSetup)}
                />
              </Route>

              <Route exact path='/setup' element={<Wizard />}>
                <Route path='/setup/logo' element={<LogoInput />} />
                <Route path='/setup/invite' element={<InviteUsers />} />
                <Route path='/setup/pipelines' element={<PipelineSetup />} />
                <Route
                  path='/setup/customer-profile'
                  element={<CustomerProfileSetup />}
                />
                <Route
                  path='/setup/custom-fields'
                  element={<CustomFieldsSetup />}
                />
                <Route path='/setup/contacts' element={<ContactTypes />} />
                <Route path='/setup/actions' element={<ActionTypes />} />
                <Route path='/setup/products' element={<Products />} />
                <Route
                  path='/setup/lead-import'
                  element={<LeadImportWizardSetup />}
                />
                <Route path='/setup/magic-button' element={<MagicButton />} />
              </Route>
            </Route>

            {/* ************** */}
            <Route path='*' element={<Navigate to='/' />} />
          </Routes>
        </StoreProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </BrowserRouter>,
  document.getElementById('app')
)
